export const BRAND_THEME_KEYS = [
  'background',
  'on-background',
  'surface',
  'on-surface',
  'surface-bright',
  'on-surface-bright',
  'surface-light',
  'on-surface-light',
  'surface-variant',
  'on-surface-variant',
  'primary',
  'on-primary',
  'secondary',
  'on-secondary',
  'accent',
]

export const BRAND_THEME_KEY_SET = new Set(BRAND_THEME_KEYS)

export const DEFAULT_BRAND_COLORS = {
  background: '#efefef',
  surface: '#F1C233',
  'surface-bright': '#123A8C',
  'on-surface-bright': '#FFFFFF',
  'surface-light': '#05163B',
  'on-surface-light': '#FFFFFF',
  'surface-variant': '#12223D',
  'on-surface-variant': '#FFFFFF',
  'on-background': '#0d2547',
  'on-surface': '#011545',
  primary: '#0b409b',
  secondary: '#144AB5',
  accent: '#F1C233',
  white: '#FFFFFF',
  'on-primary': '#FFFFFF',
  'on-secondary': '#FFFFFF',
  'on-accent': '#0d2547',
}

const VARIATION_COLORS = new Set([
  'primary', 'secondary', 'accent',
  'error', 'info', 'success', 'warning',
  'surface', 'surface-bright', 'surface-light', 'surface-variant',
])

const LIGHTEN_STEPS = 4
const DARKEN_STEPS = 4
const STYLE_ELEMENT_ID = 'sw-customizer-theme'

export function isValidHexColor(value) {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim())
}

export function sanitizeBrandTheme(theme) {
  if (!theme || typeof theme !== 'object') return {}

  return Object.entries(theme).reduce((result, [key, value]) => {
    if (BRAND_THEME_KEY_SET.has(key) && isValidHexColor(value)) {
      result[key] = value.trim()
    }
    return result
  }, {})
}

export function getHydratedBrandColors(theme = {}) {
  return {
    ...DEFAULT_BRAND_COLORS,
    ...sanitizeBrandTheme(theme),
  }
}

export function hexToRgbComponents(hex) {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  return `${r} ${g} ${b}`
}

function getStyleSheet() {
  let el = document.getElementById(STYLE_ELEMENT_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ELEMENT_ID
    document.head.appendChild(el)
  }
  return el
}

function genVariationDeclarations(colorName, hex) {
  const clean = hex.replace('#', '')
  const ri = parseInt(clean.substring(0, 2), 16)
  const gi = parseInt(clean.substring(2, 4), 16)
  const bi = parseInt(clean.substring(4, 6), 16)
  const lines = []

  for (let i = 1; i <= LIGHTEN_STEPS; i++) {
    const amt = Math.round((i / 7) * 255)
    lines.push(`  --v-theme-${colorName}-lighten-${i}: ${Math.min(ri + amt, 255)} ${Math.min(gi + amt, 255)} ${Math.min(bi + amt, 255)};`)
  }

  for (let i = 1; i <= DARKEN_STEPS; i++) {
    const amt = Math.round((i / 7) * 255)
    lines.push(`  --v-theme-${colorName}-darken-${i}: ${Math.max(ri - amt, 0)} ${Math.max(gi - amt, 0)} ${Math.max(bi - amt, 0)};`)
  }

  return lines.join('\n')
}

export function applyThemeOverrides(theme) {
  if (typeof document === 'undefined') return

  const colors = sanitizeBrandTheme(theme)
  const entries = Object.entries(colors)

  if (!entries.length) {
    clearThemeOverrides()
    return
  }

  const baseDeclarations = entries
    .map(([key, hex]) => `  --v-theme-${key}: ${hexToRgbComponents(hex)};`)
    .join('\n')

  const variationDeclarations = entries
    .filter(([key]) => VARIATION_COLORS.has(key))
    .map(([key, hex]) => genVariationDeclarations(key, hex))
    .join('\n')

  const declarations = [baseDeclarations, variationDeclarations].filter(Boolean).join('\n')
  getStyleSheet().textContent = `.v-theme--light {\n${declarations}\n}`
}

export function clearThemeOverrides() {
  if (typeof document === 'undefined') return

  const el = document.getElementById(STYLE_ELEMENT_ID)
  if (el) el.textContent = ''
}
